import { memo, useMemo } from 'react';
import { Dropdownlist } from '@/components/Dropdownlist';
import type { FormFieldShowIfData } from '@/services/api-types';
import { isOptionType, type FieldDraft } from './schema-draft';

export interface ShowIfPickerProps {
  earlierFields: FieldDraft[];
  showIf: FormFieldShowIfData | undefined;
  onChange: (next: FormFieldShowIfData | undefined) => void;
}

export const ShowIfPicker = memo(function ShowIfPicker({
  earlierFields,
  showIf,
  onChange,
}: ShowIfPickerProps) {
  const targetField = useMemo(() => {
    if (!showIf?.key) return null;
    return earlierFields.find((f) => f.key === showIf.key) ?? null;
  }, [earlierFields, showIf?.key]);

  const equalsAsString = showIf?.equals == null ? '' : String(showIf.equals);

  const fieldItems = useMemo(
    () =>
      earlierFields.map((f) => ({
        value: f.key,
        label: f.label || f.key,
      })),
    [earlierFields],
  );
  const optionItems = useMemo(
    () =>
      (targetField?.options ?? []).map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    [targetField],
  );

  if (earlierFields.length === 0) {
    return (
      <div className="kst-fb-showif">
        <span className="kst-fb-empty">
          To show this field conditionally, add at least one field earlier in the form first.
        </span>
      </div>
    );
  }

  return (
    <div className="kst-fb-showif">
      <div className="kst-fb-showif-row">
        <div className="kstc-form-group" style={{ flex: '0 0 auto' }}>
          <label className="kstc-label">When this field…</label>
          <Dropdownlist
            value={showIf?.key ?? null}
            items={fieldItems}
            sortItems={false}
            searchable={fieldItems.length > 5}
            clearable
            placeholder="Choose an earlier field…"
            onChange={(val) => {
              const key = typeof val === 'string' ? val : '';
              if (!key) {
                onChange(undefined);
              } else {
                onChange({ key, equals: showIf?.equals ?? '' });
              }
            }}
          />
        </div>
        <span className="kst-fb-showif-equals">equals</span>
        <div className="kstc-form-group" style={{ flex: '1 1 200px' }}>
          <label className="kstc-label">…this value</label>
          {targetField && isOptionType(targetField.type) ? (
            <Dropdownlist
              value={equalsAsString || null}
              items={optionItems}
              sortItems={false}
              searchable={optionItems.length > 5}
              clearable
              disabled={!showIf?.key}
              placeholder="Choose a value…"
              onChange={(val) => {
                onChange({ key: showIf!.key, equals: val == null ? '' : String(val) });
              }}
            />
          ) : (
            <input
              type="text"
              className="kstc-input"
              value={equalsAsString}
              onChange={(e) => onChange({ key: showIf!.key, equals: e.target.value })}
              placeholder='e.g. Yes, 5'
              disabled={!showIf?.key}
            />
          )}
        </div>
        {showIf && (
          <button
            type="button"
            className="kstc-btn kstc-btn--secondary"
            onClick={() => onChange(undefined)}
            title="Always show this field. Clears the condition."
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
});
